import React from 'react';
import { Check } from 'lucide-react';

/** The section's title row: heading, description, and its actions (Save Changes) on the right. */
export function SectionHead({ title, subtitle, children }) {
  return (
    <div className="st-section-head">
      <div><h2>{title}</h2><p>{subtitle}</p></div>
      {children && <div className="st-section-actions">{children}</div>}
    </div>
  );
}

/** A bordered group inside a section (Account Information, Theme, Password…). */
export function Card({ title, subtitle, icon: Icon, tone = 'green', aside, className = '', children }) {
  return (
    <section className={`st-card ${className}`}>
      {(title || aside) && (
        <div className="st-card-head">
          {Icon && <span className={`st-icon ${tone}`}><Icon size={18} /></span>}
          <div className="st-card-title">{title && <h3>{title}</h3>}{subtitle && <p>{subtitle}</p>}</div>
          {aside && <div className="st-card-aside">{aside}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Pick one: tiles with an icon / glyph, a title and a line under it; the chosen one is outlined with a tick. */
export function ChoiceCards({ options, value, onChange, columns = 3, name, layout = 'row', disabled }) {
  return (
    <div className={`st-choices cols-${columns} ${layout}`} role="radiogroup" aria-label={name}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button type="button" key={String(o.value)} role="radio" aria-checked={on} disabled={disabled}
            className={`st-option ${on ? 'on' : ''}`} onClick={() => !on && onChange(o.value)}>
            {o.glyph && <span className="st-option-glyph" style={o.glyphStyle}>{o.glyph}</span>}
            <span className="st-option-copy"><b>{o.label}</b>{o.sub && <small>{o.sub}</small>}</span>
            {on && <span className="st-tick"><Check size={12} strokeWidth={3} /></span>}
          </button>
        );
      })}
    </div>
  );
}

/** An on / off switch. */
export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled}
      className={`st-switch ${checked ? 'on' : ''}`} onClick={() => onChange(!checked)}>
      <i />
    </button>
  );
}

/** A setting with a switch: icon, title, description, and the switch (or other control) on the right. */
export function ToggleRow({ icon: Icon, tone = 'blue', title, sub, checked, onChange, disabled, children }) {
  return (
    <div className="st-toggle-row">
      {Icon && <span className={`st-icon small ${tone}`}><Icon size={16} /></span>}
      <span className="st-toggle-copy"><b>{title}</b>{sub && <small>{sub}</small>}</span>
      {children || <Toggle checked={checked} onChange={onChange} label={title} disabled={disabled} />}
    </div>
  );
}

/** Label above an input / select, with an optional icon inside the control. */
export function Field({ label, icon: Icon, hint, children, span }) {
  return (
    <label className={`st-field ${span ? 'span-2' : ''}`}>
      <span className="st-label">{label}</span>
      <span className={`st-control ${Icon ? 'with-icon' : ''}`}>{Icon && <Icon size={16} />}{children}</span>
      {hint && <small className="st-hint">{hint}</small>}
    </label>
  );
}
